/*!

=========================================================
* Argon Dashboard React - v1.2.4
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Col,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { auth, db } from "data/firebase";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { setAuthUser } from "components/Auth/authStore";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInAnonymously,
  signInWithPopup,
} from "firebase/auth";
import {
  toE164TR,
  toFriendlyPhoneAuthError,
  usePhoneOtpFirebase,
} from "components/Auth/usePhoneOtpFirebase";
import { getAuthUser } from "components/Auth/authStore";

const COUNTRY_OPTIONS = [
  "TR",
  ...getCountries().filter((c) => c !== "TR").sort(),
];

const Login = () => {
  const navigate = useNavigate();

  const APP_BASE_RAW = process.env.PUBLIC_URL || "";
  const APP_BASE =
    APP_BASE_RAW && APP_BASE_RAW !== "/" ? APP_BASE_RAW.replace(/\/+$/, "") : "";
  const [country, setCountry] = useState("TR");
  const [nationalNumber, setNationalNumber] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [step, setStep] = useState("phone");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [isSmsLoading, setIsSmsLoading] = useState(false);
  const [isGuestLoading, setIsGuestLoading] = useState(false);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [termsModalAccepted, setTermsModalAccepted] = useState(false);
  const [pendingOAuthProvider, setPendingOAuthProvider] = useState(null);

  const phoneOtp = usePhoneOtpFirebase({ auth, recaptchaButtonId: "sign-in-button" });

  const goApp = () => navigate(`${APP_BASE}/admin/index`, { replace: true });
  const goGuest = () => navigate(`${APP_BASE}/admin/index`, { replace: true });

  useEffect(() => {
    const existingUser = getAuthUser();
    if (existingUser) {
      navigate(`${APP_BASE}/admin/index`, { replace: true });
    }
  }, [navigate, APP_BASE]);

  const dialCode = (() => {
    try {
      return `+${getCountryCallingCode(country)}`;
    } catch {
      return "+90";
    }
  })();

  const phoneE164 = toE164TR({ dialCode, nationalNumber });

  useEffect(() => {
    (async () => {
      try {
        await phoneOtp.initRecaptcha();
      } catch {}
    })();
  }, [phoneOtp]);

  const phoneKey = (value) => String(value ?? "").replace(/[^0-9]/g, "");

  const checkRegistered = async (e164) => {
    try {
      const key = phoneKey(e164);
      const snap = await getDoc(doc(db, "usersByPhone", key));
      return snap.exists();
    } catch {
      return null;
    }
  };

  const onPhoneSendSms = async () => {
    setError("");
    if (!phoneE164) {
      setError("Lütfen telefon numaranızı girin.");
      return;
    }

    setIsChecking(true);
    await checkRegistered(phoneE164);
    setIsChecking(false);

    setIsSmsLoading(true);

    try {
      const result = await phoneOtp.sendCode(phoneE164);
      setConfirmationResult(result);
      setStep("code");
    } catch (e) {
      console.error("PhoneAuth sendSms error:", e);

      const code = e?.code ? String(e.code) : "";
      if (
        code.includes("captcha") ||
        code.includes("invalid-app-credential") ||
        code.includes("invalid-recaptcha-token") ||
        code.includes("auth/internal-error")
      ) {
        try {
          await phoneOtp.resetRecaptcha();
        } catch {}
      }

      setError(`${toFriendlyPhoneAuthError(e)}${code ? ` (Hata: ${code})` : ""}`);
    } finally {
      setIsSmsLoading(false);
    }
  };

  const onPhoneVerifyCode = async () => {
    setError("");
    if (!confirmationResult) {
      setError("Önce SMS gönderin.");
      return;
    }
    const code = String(smsCode ?? "").trim();
    if (!code) {
      setError("Lütfen SMS doğrulama kodunu girin.");
      return;
    }

    try {
      const credential = await phoneOtp.verifyCode(code);
      const user = credential?.user;
      const e164 = user?.phoneNumber || phoneE164;
      const uid = user?.uid;

      if (uid) {
        const byPhoneId = phoneKey(e164);
        const byPhoneRef = doc(db, "usersByPhone", byPhoneId);
        const byPhoneSnap = await getDoc(byPhoneRef);
        if (!byPhoneSnap.exists()) {
          await setDoc(
            byPhoneRef,
            {
              uid,
              phone: e164,
              createdAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        const userRef = doc(db, "users", uid);
        await setDoc(
          userRef,
          {
            uid,
            phone: e164,
            provider: "phone",
            termsAcceptedAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
          },
          { merge: true }
        );

        let role = "editor";
        try {
          const userSnap = await getDoc(userRef);
          const existingRole = userSnap.exists() ? userSnap.data()?.role : null;
          if (existingRole) {
            role = existingRole;
          } else {
            await setDoc(userRef, { role: "editor" }, { merge: true });
          }
        } catch {}

        setAuthUser({
          id: uid,
          provider: "phone",
          phone: e164,
          role,
          createdAt: new Date().toISOString(),
        });
      } else {
        setAuthUser({
          id: uid || `phone:${e164}`,
          provider: "phone",
          phone: e164,
          role: "editor",
          createdAt: new Date().toISOString(),
        });
      }
      try {
        phoneOtp.clearRecaptcha();
      } catch {}
      goApp();
    } catch (e) {
      const code = e?.code ? String(e.code) : "";
      setError(
        `Kod doğrulanamadı. Lütfen tekrar deneyin.${code ? ` (Hata: ${code})` : ""}`
      );
    }
  };

  const onFirebaseProviderContinue = async (providerName) => {
    setError("");
    if (isOAuthLoading) return;
    setIsOAuthLoading(true);
    try {
      let provider;
      if (providerName === "facebook") {
        provider = new FacebookAuthProvider();
        provider.addScope("email");
        provider.addScope("public_profile");
      } else if (providerName === "google") {
        provider = new GoogleAuthProvider();
        provider.addScope("email");
        provider.addScope("profile");
      } else {
        throw new Error("Desteklenmeyen sağlayıcı");
      }

      const credential = await signInWithPopup(auth, provider);
      const user = credential?.user;
      if (!user?.uid) {
        throw new Error("Giriş yapılamadı (kullanıcı bilgisi yok)");
      }

      const providerId = providerName === "facebook" ? "facebook.com" : "google.com";
      const providerData =
        (Array.isArray(user.providerData)
          ? user.providerData.find((p) => p?.providerId === providerId) || user.providerData[0]
          : null) || null;

      let email = user.email || providerData?.email || null;
      let displayName = user.displayName || providerData?.displayName || null;

      if (providerName === "facebook" && (!email || !displayName)) {
        try {
          const fbCred = FacebookAuthProvider.credentialFromResult(credential);
          const accessToken = fbCred?.accessToken || null;
          if (accessToken) {
            const resp = await fetch(
              `https://graph.facebook.com/me?fields=id,name,email&access_token=${encodeURIComponent(
                accessToken
              )}`
            );
            if (resp.ok) {
              const data = await resp.json();
              if (!displayName && data?.name) displayName = String(data.name);
              if (!email && data?.email) email = String(data.email);
            }
          }
        } catch {}
      }

      const fullName = displayName || null;

      let firstName = null;
      let lastName = null;
      if (fullName) {
        const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
          firstName = parts[0];
          lastName = parts.slice(1).join(" ");
        } else if (parts.length === 1) {
          firstName = parts[0];
        }
      }

      const userDoc = {
        uid: user.uid,
        provider: providerName,
        phone: user.phoneNumber || null,
        termsAcceptedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      };

      if (email) userDoc.email = email;
      if (displayName) userDoc.displayName = displayName;
      if (fullName) userDoc.fullName = fullName;
      if (firstName) userDoc.firstName = firstName;
      if (lastName) userDoc.lastName = lastName;

      await setDoc(
        doc(db, "users", user.uid),
        userDoc,
        { merge: true }
      );

      try {
        const profileDoc = {
          uid: user.uid,
          provider: providerName,
          updatedAt: serverTimestamp(),
        };
        if (fullName) profileDoc.fullName = fullName;
        if (email) profileDoc.email = email;
        await setDoc(doc(db, "userProfiles", user.uid), profileDoc, { merge: true });
      } catch {}

      let role = "editor";
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const existingRole = userSnap.exists() ? userSnap.data()?.role : null;
        if (existingRole) {
          role = existingRole;
        } else {
          await setDoc(userRef, { role: "editor" }, { merge: true });
        }
      } catch {}

      setAuthUser({
        id: user.uid,
        provider: providerName,
        phone: user.phoneNumber || null,
        email,
        displayName,
        role,
        createdAt: new Date().toISOString(),
      });

      goApp();
    } catch (e) {
      console.error("OAuth login error:", e);
      const code = e?.code ? String(e.code) : "";

      let msg = "Giriş yapılamadı. Firebase Auth sağlayıcı ayarlarını kontrol edin.";
      if (code.includes("auth/popup-blocked")) {
        msg = "Popup engellendi. Tarayıcı popup engelini kaldırıp tekrar deneyin.";
      } else if (code.includes("auth/popup-closed-by-user")) {
        msg = "Giriş penceresi kapatıldı. Tekrar deneyin.";
      } else if (code.includes("auth/operation-not-allowed")) {
        msg = "Bu giriş yöntemi aktif değil. Firebase Console > Authentication > Sign-in method'tan etkinleştirin.";
      } else if (code.includes("auth/unauthorized-domain")) {
        msg = "Bu domain yetkili değil. Firebase Console > Authentication > Settings > Authorized domains içine ekleyin.";
      } else if (
        code.includes("auth/invalid-credential-or-provider-id") ||
        code.includes("auth/invalid-idp-response")
      ) {
        msg =
          "Facebook/Google sağlayıcı yanıtı geçersiz. Facebook tarafında App Domains ve Valid OAuth Redirect URIs ayarları eksik/yanlış olabilir. Facebook Developers > Settings > Basic: App Domains'a domainini ekle; Facebook Login > Settings: Valid OAuth Redirect URIs'ye Firebase handler URL'lerini ekle (örn. https://destinationblacksea-6d8b6.firebaseapp.com/__/auth/handler ve https://destinationblacksea-6d8b6.web.app/__/auth/handler).";
      }

      setError(`${msg}${code ? ` (Hata: ${code})` : ""}`);
    } finally {
      setIsOAuthLoading(false);
    }
  };

  const onTermsAcceptedChange = (e) => {
    setTermsAccepted(Boolean(e?.target?.checked));
  };

  const openTermsModalForProvider = (providerName) => {
    setError("");
    setPendingOAuthProvider(providerName);
    setTermsModalAccepted(Boolean(termsAccepted));
    setIsTermsModalOpen(true);
  };

  const closeTermsModal = () => {
    setIsTermsModalOpen(false);
    setPendingOAuthProvider(null);
  };

  const onTermsModalContinue = async () => {
    if (!termsModalAccepted) return;
    setTermsAccepted(true);

    const providerName = pendingOAuthProvider;
    closeTermsModal();
    if (!providerName) return;

    if (providerName === "guest") {
      await performGuestLogin();
      return;
    }

    await onFirebaseProviderContinue(providerName);
  };

  const performGuestLogin = async () => {
    setError("");
    if (isGuestLoading) return;
    setIsGuestLoading(true);
    try {
      const credential = await signInAnonymously(auth);
      const user = credential?.user;
      if (!user?.uid) throw new Error("Misafir giriş yapılamadı");

      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          uid: user.uid,
          provider: "anonymous",
          role: "guest",
          isAnonymous: true,
          lastLoginAt: serverTimestamp(),
        },
        { merge: true }
      );

      setAuthUser({
        id: user.uid,
        provider: "anonymous",
        role: "guest",
        createdAt: new Date().toISOString(),
      });
      goGuest();
    } catch (e) {
      console.error("Anonymous login error:", e);

      const code = e?.code ? String(e.code) : "";
      if (code.includes("auth/operation-not-allowed")) {
        setError(
          "Misafir girişi Firebase tarafında kapalı. Firebase Console > Authentication > Sign-in method > Anonymous etkinleştirin."
        );
      } else {
        setError("Misafir girişi yapılamadı. Lütfen tekrar deneyin.");
      }
    } finally {
      setIsGuestLoading(false);
    }
  };

  const onGuestContinue = () => {
    performGuestLogin();
  };

  return (
    <>
      <Col xs="12" sm="10" md="7" lg="5">
        <Card className="bg-secondary shadow border-0">
          <CardBody className="px-lg-5 py-lg-5">
            {step === "phone" ? (
              <Button
                color="default"
                outline
                type="button"
                className="mb-3"
                style={{ width: "100%" }}
                disabled={isChecking || isSmsLoading || isOAuthLoading || isGuestLoading}
                onClick={onGuestContinue}
              >
                {isGuestLoading ? "Misafir girişi yapılıyor…" : "Misafir olarak giriş yap"}
              </Button>
            ) : null}
            <div className="text-center text-default mb-4">
              <small>
                Telefon numaranızla devam edin (hesabınız yoksa otomatik
                oluşturulur)
              </small>
            </div>
            {error ? (
              <div className="text-center text-danger mb-3" style={{ wordBreak: "break-word" }}>
                <small>{error}</small>
              </div>
            ) : null}
            <Form role="form">
              {step === "phone" ? (
                <FormGroup className="mb-3">
                  <InputGroup className="input-group-alternative">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="ni ni-mobile-button mr-2" />
                        <select
                          className="bg-transparent border-0"
                          aria-label="Ülke kodu"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                        >
                          {COUNTRY_OPTIONS.map((c) => (
                            <option key={c} value={c}>
                              {c} (+{getCountryCallingCode(c)})
                            </option>
                          ))}
                        </select>
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      placeholder="530 XXX XX XX"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={nationalNumber}
                      onChange={(e) => setNationalNumber(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              ) : (
                <FormGroup className="mb-3">
                  <InputGroup className="input-group-alternative">
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="ni ni-key-25" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      placeholder="SMS Kodu"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={smsCode}
                      onChange={(e) => setSmsCode(e.target.value)}
                    />
                  </InputGroup>
                </FormGroup>
              )}

              {}
              {step === "phone" && (
                <div className="mt-3 mb-2 text-center">
                  <div
                    className="custom-control custom-control-alternative custom-checkbox"
                    style={{ display: "inline-block" }}
                  >
                    <input
                      className="custom-control-input"
                      id="terms-checkbox"
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={onTermsAcceptedChange}
                    />
                    <label
                      className="custom-control-label"
                      htmlFor="terms-checkbox"
                      style={{ cursor: "pointer", fontSize: 13 }}
                    >
                      <span className="text-default">
                        <Link
                          className="text-default"
                          to="/auth/terms-of-service"
                          onClick={(evt) => evt.stopPropagation()}
                        >
                          Üyelik Sözleşmesi
                        </Link>
                        <span>’ni okudum ve kabul ediyorum</span>
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <div
                className="mt-2"
                style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 10 }}
              >
                <Button
                    id="sign-in-button"
                  className="my-4"
                  color="primary"
                  type="button"
                    disabled={isChecking || isSmsLoading || (!termsAccepted && step === "phone")}
                  onClick={step === "phone" ? onPhoneSendSms : onPhoneVerifyCode}
                >
                    {step === "phone"
                      ? isSmsLoading
                        ? "Gönderiliyor…"
                        : "SMS Gönder"
                      : "Doğrula ve Devam Et"}
                </Button>
                {step === "code" ? (
                  <Button
                    className="mb-4"
                    color="secondary"
                    type="button"
                    onClick={() => {
                      setStep("phone");
                      setSmsCode("");
                      setConfirmationResult(null);
                    }}
                  >
                    Geri
                  </Button>
                ) : null}
              </div>

              <div className="mt-2">
                <div className="text-default text-center mt-2 mb-3">
                  <small>Şununla giriş yap</small>
                </div>
                <div
                  className="btn-wrapper"
                  style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10 }}
                >
                  <Button
                    className="btn-neutral btn-icon"
                    color="default"
                    href="#pablo"
                    disabled={isOAuthLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      openTermsModalForProvider("facebook");
                    }}
                  >
                    <span className="btn-inner--icon">
                      <i className="fab fa-facebook-f" />
                    </span>
                    <span className="btn-inner--text">Facebook</span>
                  </Button>
                  <Button
                    className="btn-neutral btn-icon"
                    color="default"
                    href="#pablo"
                    disabled={isOAuthLoading}
                    onClick={(e) => {
                      e.preventDefault();
                      openTermsModalForProvider("google");
                    }}
                  >
                    <span className="btn-inner--icon">
                      <img
                        alt="..."
                        src={
                          require("../../assets/img/icons/common/google.svg")
                            .default
                        }
                      />
                    </span>
                    <span className="btn-inner--text">Google</span>
                  </Button>
                </div>
              </div>
            </Form>
          </CardBody>
        </Card>

        <Modal isOpen={isTermsModalOpen} toggle={closeTermsModal} centered>
          <ModalHeader toggle={closeTermsModal}>Üyelik Sözleşmesi</ModalHeader>
          <ModalBody>
            <div className="text-default" style={{ fontSize: 14 }}>
              Devam etmek için
              <span className="mx-1">
                <Link to="/auth/terms-of-service">Üyelik Sözleşmesi</Link>
              </span>
              ’ni okuduğunuzu ve kabul ettiğinizi onaylayın.
            </div>

            <div className="custom-control custom-control-alternative custom-checkbox mt-3">
              <input
                className="custom-control-input"
                id="terms-modal-checkbox"
                type="checkbox"
                checked={termsModalAccepted}
                onChange={(e) => setTermsModalAccepted(Boolean(e?.target?.checked))}
              />
              <label
                className="custom-control-label"
                htmlFor="terms-modal-checkbox"
                style={{ cursor: "pointer", fontSize: 13 }}
              >
                <span className="text-default">Okudum ve kabul ediyorum</span>
              </label>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={closeTermsModal}>
              İptal
            </Button>
            <Button
              color="primary"
              type="button"
              disabled={!termsModalAccepted || isOAuthLoading}
              onClick={onTermsModalContinue}
            >
              Kabul Et ve Devam Et
            </Button>
          </ModalFooter>
        </Modal>
      </Col>
    </>
  );
};

export default Login;
